import React, { useState, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { 
  AlertCircle, Clock, RefreshCw, User, Tag, ArrowRight, Activity, 
  TrendingDown, Plus, Search, MessageCircle, Trash2, CheckCircle2, 
  Filter, ChevronRight, Phone, Sparkles, X, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const MOCK_LEADS = [
  { id: '1', name: 'João Silva', phone: '11 9999-0001', status: 'novo', createdAt: new Date(Date.now() - 20 * 60000).toISOString(), source: 'facebook', utm: '?utm_source=meta_ads', assignedTo: 'Pedro' },
  { id: '2', name: 'Maria Souza', phone: '11 9999-0002', status: 'novo', createdAt: new Date(Date.now() - 5 * 60000).toISOString(), source: 'google', utm: '?utm_source=google_ads', assignedTo: 'Maria' },
  { id: '3', name: 'Carlos Santos', phone: '11 9999-0003', status: 'apresentacao', createdAt: new Date(Date.now() - 2 * 86400000).toISOString(), source: 'whatsapp', assignedTo: 'João' },
  { id: '4', name: 'Ana Oliveira', phone: '11 9999-0004', status: 'perdido', createdAt: new Date(Date.now() - 32 * 86400000).toISOString(), source: 'google', assignedTo: 'Pedro' },
  { id: '5', name: 'Lucas Lima', phone: '11 9999-0005', isDuplicate: true, status: 'novo', createdAt: new Date(Date.now() - 40 * 60000).toISOString(), source: 'facebook', utm: '?utm_source=meta_ads', assignedTo: 'Maria' },
];

const generateUniqueLeadId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `lead_${Math.random().toString(36).substring(2, 9)}`;
};

export function SmartCrmBoard() {
  const { leads, setLeads, addLead, updateLead, appSettings, responsibles } = useStore();
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('todos');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [nowTime] = useState(() => Date.now());

  // Form State for new lead
  const [newLeadName, setNewLeadName] = useState('');
  const [newLeadPhone, setNewLeadPhone] = useState('');
  const [newLeadSource, setNewLeadSource] = useState<'facebook' | 'google' | 'whatsapp' | 'organico'>('whatsapp');
  const [newLeadStatus, setNewLeadStatus] = useState('novo');
  const [newLeadAssigned, setNewLeadAssigned] = useState('');

  useEffect(() => {
    if (!leads || leads.length === 0) {
      setLeads(MOCK_LEADS);
    }
  }, [leads, setLeads]);

  // Calculate next rep
  const activeReps = responsibles.filter(r => r.nome.trim() !== '');
  let nextRepName = 'Nenhum vendedor configurado';
  if (activeReps.length > 0) {
    const nextIndex = (appSettings.lastAssignedRepIndex + 1) % activeReps.length;
    nextRepName = activeReps[nextIndex].nome || 'Vendedor ' + (nextIndex + 1);
  }

  const lastLogin = new Date(appSettings?.lastLoginAt || nowTime);
  const diffDays = Math.floor((nowTime - lastLogin.getTime()) / 86400000);
  const showHealthRisk = diffDays >= 7;
  const totalLeads = leads?.length || 0;
  const showUpsell = totalLeads >= 1000;

  const columns = [
    { id: 'novo', title: 'Novos Leads', color: 'border-blue-400 bg-blue-50/30' },
    { id: 'apresentacao', title: 'Apresentação / Contato', color: 'border-purple-400 bg-purple-50/30' },
    { id: 'negociacao', title: 'Em Negociação', color: 'border-amber-400 bg-amber-50/30' },
    { id: 'fechamento', title: 'Fechamento / Proposta', color: 'border-indigo-400 bg-indigo-50/30' },
    { id: 'ganho', title: 'Venda Fechada (Ganhos)', color: 'border-emerald-400 bg-emerald-50/30' },
    { id: 'perdido', title: 'Perdidos / Desistência', color: 'border-rose-400 bg-rose-50/30' },
  ];

  const getLeadsByStatus = (status: string) => {
    return (leads || []).filter(l => {
      const matchesStatus = l.status === status;
      const matchesSearch = searchTerm.trim() === '' || 
        l.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        l.phone.includes(searchTerm);
      const matchesSource = sourceFilter === 'todos' || l.source === sourceFilter;
      return matchesStatus && matchesSearch && matchesSource;
    });
  };

  const calculateGargalo = () => {
    const counts = {
      novo: (leads || []).filter(l => l.status === 'novo').length,
      apresentacao: (leads || []).filter(l => l.status === 'apresentacao').length,
      negociacao: (leads || []).filter(l => l.status === 'negociacao').length,
      fechamento: (leads || []).filter(l => l.status === 'fechamento').length,
      ganho: (leads || []).filter(l => l.status === 'ganho').length,
      perdido: (leads || []).filter(l => l.status === 'perdido').length
    };
    
    const funnel = [
      { name: 'Novo', count: counts.novo },
      { name: 'Apresentação', count: counts.apresentacao },
      { name: 'Negociação', count: counts.negociacao },
      { name: 'Fechamento', count: counts.fechamento }
    ];
    
    let worstDrop = 0;
    let worstStepName = 'Apresentação';
    
    for (let i = 0; i < funnel.length - 1; i++) {
      const current = funnel[i].count;
      const next = funnel[i+1].count;
      if (current > 0) {
        const drop = Math.round(((current - next) / current) * 100);
        if (drop > worstDrop) {
          worstDrop = drop;
          worstStepName = funnel[i+1].name;
        }
      }
    }
    
    if (worstDrop === 0) return { drop: 40, step: 'Apresentação' };
    return { drop: worstDrop, step: worstStepName };
  };

  const [now, setNow] = useState<number>(Date.now);
  
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  const isFrio = (lead: any) => {
    const dateStr = lead.updatedAt || lead.createdAt;
    if (now === 0 || !dateStr) return false;
    const diffMins = (now - new Date(dateStr).getTime()) / 60000;
    return diffMins > 15;
  };

  const isAntigo = (lead: any) => {
    const dateStr = lead.updatedAt || lead.createdAt;
    if (now === 0 || !dateStr) return false;
    const diffDays = (now - new Date(dateStr).getTime()) / 86400000;
    return diffDays >= 30;
  };

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedLeadId(id);
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    const id = draggedLeadId || e.dataTransfer.getData('text/plain');
    if (id) {
      updateLead(id, { status: targetStatus, updatedAt: new Date().toISOString() });
      toast.success(`Lead movido para ${columns.find(c => c.id === targetStatus)?.title}!`);
      setDraggedLeadId(null);
    }
  };

  const handleMoveStage = (leadId: string, currentStatus: string, direction: 'prev' | 'next') => {
    const currentIndex = columns.findIndex(c => c.id === currentStatus);
    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex >= 0 && newIndex < columns.length) {
      const nextCol = columns[newIndex];
      updateLead(leadId, { status: nextCol.id, updatedAt: new Date().toISOString() });
      toast.success(`Movido para ${nextCol.title}`);
    }
  };

  const handleWhatsAppChat = (lead: any) => {
    const cleanPhone = lead.phone.replace(/\D/g, '');
    const phoneWithCountry = cleanPhone.length <= 11 ? `55${cleanPhone}` : cleanPhone;
    const message = encodeURIComponent(`Olá ${lead.name}, tudo bem? Aqui é da equipe de atendimento.`);
    window.open(`https://wa.me/${phoneWithCountry}?text=${message}`, '_blank');
  };

  const handleDeleteLead = (leadId: string) => {
    const filtered = (leads || []).filter(l => l.id !== leadId);
    setLeads(filtered);
    toast.success('Lead removido do pipeline.');
  };

  const handleCreateLead = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLeadName.trim()) {
      toast.error('Informe o nome do lead');
      return;
    }

    const assigned = newLeadAssigned || nextRepName;
    const newLead = {
      id: generateUniqueLeadId(),
      name: newLeadName.trim(),
      phone: newLeadPhone.trim() || '11 9999-9999',
      status: newLeadStatus,
      source: newLeadSource,
      utm: newLeadSource === 'facebook' ? '?utm_source=meta_ads' : newLeadSource === 'google' ? '?utm_source=google_ads' : undefined,
      assignedTo: assigned,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    addLead(newLead);
    toast.success(`Lead "${newLead.name}" cadastrado com sucesso!`);
    
    // Reset form
    setNewLeadName('');
    setNewLeadPhone('');
    setNewLeadStatus('novo');
    setIsAddModalOpen(false);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* Header & Quick Action Bar */}
      <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <span>Smart CRM & Funil de Vendas</span>
            <span className="text-xs bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold border border-indigo-200">
              Kanban Ao Vivo
            </span>
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Distribuição de leads, rastreamento de origem, alerta de esfriamento e transição comercial.
          </p>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="flex-1 md:flex-none bg-black hover:bg-gray-800 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm flex items-center justify-center gap-2"
          >
            <Plus size={16} />
            Novo Lead
          </button>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
        <div className="relative w-full sm:w-80">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text"
            placeholder="Buscar por nome ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto">
          <Filter size={15} className="text-gray-400 shrink-0" />
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider shrink-0">Origem:</span>
          {(['todos', 'whatsapp', 'google', 'facebook'] as const).map((source) => (
            <button
              key={source}
              onClick={() => setSourceFilter(source)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                sourceFilter === source 
                  ? 'bg-gray-900 text-white shadow-sm' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {source === 'todos' && 'Todas as Fontes'}
              {source === 'whatsapp' && 'WhatsApp'}
              {source === 'google' && 'Google Ads'}
              {source === 'facebook' && 'Meta Ads'}
            </button>
          ))}
        </div>
      </div>

      {/* Inteligência Comportamental & Alertas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Identificador de Gargalo */}
        <div className="bg-red-50/80 border border-red-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
          <div className="p-2 bg-red-100 rounded-lg text-red-600 shrink-0">
            <TrendingDown size={20} />
          </div>
          <div>
            <h4 className="text-red-950 font-bold text-sm">Gargalo do Funil</h4>
            <p className="text-xs text-red-800 mt-0.5">
              Quebra de <strong>{calculateGargalo().drop}%</strong> na etapa <strong>{calculateGargalo().step}</strong>. Oportunidade de ajuste no script de abordagem.
            </p>
          </div>
        </div>

        {/* Roleta de Vendedores */}
        <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
          <div className="p-2 bg-blue-100 rounded-lg text-blue-600 shrink-0">
            <User size={20} />
          </div>
          <div>
            <h4 className="text-blue-950 font-bold text-sm">Roleta Ativa (Round-Robin)</h4>
            <p className="text-xs text-blue-800 mt-0.5">
              Próximo lead atribuído para: <strong>{nextRepName}</strong>.
            </p>
          </div>
        </div>

        {/* Injeção de UTM */}
        <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
          <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600 shrink-0">
            <Tag size={20} />
          </div>
          <div>
            <h4 className="text-emerald-950 font-bold text-sm">Rastreamento X9</h4>
            <p className="text-xs text-emerald-800 mt-0.5">
              UTMs rastreiam a campanha de origem de cada contato em tempo real.
            </p>
          </div>
        </div>
      </div>

      {/* Kanban Board Container */}
      <div className="flex gap-4 overflow-x-auto pb-6 min-h-[640px] items-start hide-scrollbar">
        {columns.map((col, colIdx) => {
          const colLeads = getLeadsByStatus(col.id);
          return (
            <div 
              key={col.id} 
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, col.id)}
              className="min-w-[310px] w-[310px] bg-gray-50/80 rounded-2xl border border-gray-200 flex flex-col shrink-0 shadow-sm overflow-hidden"
            >
              {/* Column Header */}
              <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white">
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${
                    col.id === 'novo' ? 'bg-blue-500' :
                    col.id === 'apresentacao' ? 'bg-purple-500' :
                    col.id === 'negociacao' ? 'bg-amber-500' :
                    col.id === 'fechamento' ? 'bg-indigo-500' :
                    col.id === 'ganho' ? 'bg-emerald-500' : 'bg-rose-500'
                  }`}></span>
                  <h3 className="font-bold text-gray-900 text-sm">{col.title}</h3>
                </div>
                <span className="bg-gray-100 text-gray-700 text-xs font-bold px-2.5 py-0.5 rounded-full border border-gray-200">
                  {colLeads.length}
                </span>
              </div>
              
              {/* Column Cards */}
              <div className="p-3 flex-1 space-y-3 min-h-[500px]">
                {colLeads.length > 0 ? (
                  colLeads.map(lead => {
                    const frio = col.id === 'novo' && isFrio(lead);
                    const reciclagem = col.id === 'perdido' && isAntigo(lead);

                    return (
                      <div 
                        key={lead.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, lead.id)}
                        className={`bg-white border rounded-xl p-4 shadow-sm relative transition-all hover:shadow-md cursor-grab active:cursor-grabbing ${
                          frio ? 'border-red-400 ring-2 ring-red-400/40 animate-pulse' : 'border-gray-200'
                        } ${reciclagem ? 'border-amber-400 ring-2 ring-amber-400/40' : ''}`}
                      >
                        {lead.isDuplicate && (
                          <div className="absolute top-0 right-0 bg-purple-600 text-white text-[9px] font-bold px-2.5 py-0.5 rounded-bl-lg flex items-center gap-1 z-10">
                            <AlertCircle size={10} /> TELEFONE DUPLICADO
                          </div>
                        )}
                        
                        {frio && !lead.isDuplicate && (
                          <div className="absolute top-0 right-0 bg-red-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-bl-lg flex items-center gap-1">
                            <AlertCircle size={10} /> LEAD FRIO {'>'} 15 MIN
                          </div>
                        )}
                        
                        {reciclagem && (
                          <div className="absolute top-0 left-0 right-0 bg-amber-400 text-black text-[9px] font-bold px-2 py-0.5 flex justify-center items-center gap-1 rounded-t-xl">
                            <RefreshCw size={10} /> RECICLAGEM RECOMENDADA
                          </div>
                        )}

                        <div className={`${frio || reciclagem ? 'mt-2' : ''}`}>
                          <div className="flex items-start justify-between">
                            <h4 className="font-bold text-gray-900 text-sm leading-tight">{lead.name}</h4>
                            <button 
                              onClick={() => handleDeleteLead(lead.id)}
                              className="text-gray-300 hover:text-red-500 transition-colors p-1"
                              title="Remover Lead"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>

                          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1 font-mono">
                            <Phone size={12} className="text-gray-400" />
                            {lead.phone}
                          </p>
                          
                          {/* Tags & Sources */}
                          <div className="flex flex-wrap gap-1.5 my-3">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${
                              lead.source === 'facebook' ? 'bg-blue-100 text-blue-700' :
                              lead.source === 'google' ? 'bg-red-100 text-red-700' :
                              lead.source === 'whatsapp' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {lead.source}
                            </span>
                            
                            {lead.utm && (
                              <span className="text-[9px] font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                                {lead.utm}
                              </span>
                            )}
                          </div>

                          {/* Responsável & Tempo */}
                          <div className="flex items-center justify-between pt-2.5 border-t border-gray-100 text-[11px] text-gray-500">
                            <span className="flex items-center gap-1 truncate font-medium">
                              <User size={12} className="text-gray-400" />
                              {lead.assignedTo || 'Não atribuído'}
                            </span>
                            <span className="flex items-center gap-1 text-gray-400 text-[10px]">
                              <Clock size={11} />
                              {new Date(lead.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </span>
                          </div>

                          {/* Quick Action Buttons */}
                          <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center gap-2">
                            <button
                              onClick={() => handleWhatsAppChat(lead)}
                              className="flex-1 bg-[#25D366] hover:bg-[#128C7E] text-white text-xs font-bold py-1.5 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-colors shadow-sm"
                            >
                              <MessageCircle size={13} />
                              WhatsApp
                            </button>

                            {/* Move stage controls */}
                            <div className="flex items-center gap-1">
                              {colIdx > 0 && (
                                <button
                                  onClick={() => handleMoveStage(lead.id, lead.status, 'prev')}
                                  className="p-1.5 text-gray-500 hover:text-black hover:bg-gray-100 rounded-md transition-colors"
                                  title="Voltar Etapa"
                                >
                                  <ArrowLeft size={13} />
                                </button>
                              )}
                              {colIdx < columns.length - 1 && (
                                <button
                                  onClick={() => handleMoveStage(lead.id, lead.status, 'next')}
                                  className="p-1.5 text-gray-500 hover:text-black hover:bg-gray-100 rounded-md transition-colors"
                                  title="Avançar Etapa"
                                >
                                  <ChevronRight size={14} />
                                </button>
                              )}
                            </div>
                          </div>

                          {reciclagem && (
                            <button 
                              onClick={() => {
                                updateLead(lead.id, { status: 'novo', updatedAt: new Date().toISOString() });
                                toast.success('Lead reaquecido e enviado para Novos Leads!');
                              }} 
                              className="w-full mt-2.5 flex items-center justify-center gap-1 bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-bold py-1.5 rounded-lg transition-colors"
                            >
                              <RefreshCw size={12} /> Reaquecer Lead
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="h-32 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-center p-4 text-gray-400">
                    <p className="text-xs font-medium">Nenhum lead nesta etapa</p>
                    <p className="text-[10px] mt-0.5">Arraste um card para cá</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal: Adicionar Novo Lead */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-black text-white flex items-center justify-center">
                  <Plus size={16} />
                </div>
                <h3 className="font-bold text-gray-900 text-lg">Novo Lead</h3>
              </div>
              <button 
                onClick={() => setIsAddModalOpen(false)}
                className="text-gray-400 hover:text-gray-900 p-1.5 rounded-lg transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateLead} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Nome Completo
                </label>
                <input 
                  type="text"
                  required
                  placeholder="Ex: Roberto Almeida"
                  value={newLeadName}
                  onChange={(e) => setNewLeadName(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Telefone / WhatsApp
                </label>
                <input 
                  type="text"
                  required
                  placeholder="Ex: 11 98765-4321"
                  value={newLeadPhone}
                  onChange={(e) => setNewLeadPhone(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                    Origem
                  </label>
                  <select
                    value={newLeadSource}
                    onChange={(e: any) => setNewLeadSource(e.target.value)}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                  >
                    <option value="whatsapp">WhatsApp</option>
                    <option value="facebook">Meta Ads</option>
                    <option value="google">Google Ads</option>
                    <option value="organico">Orgânico / Site</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                    Estágio Inicial
                  </label>
                  <select
                    value={newLeadStatus}
                    onChange={(e) => setNewLeadStatus(e.target.value)}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                  >
                    <option value="novo">Novos Leads</option>
                    <option value="apresentacao">Apresentação</option>
                    <option value="negociacao">Em Negociação</option>
                    <option value="fechamento">Fechamento</option>
                    <option value="ganho">Venda Fechada</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Atribuir para Vendedor
                </label>
                <select
                  value={newLeadAssigned}
                  onChange={(e) => setNewLeadAssigned(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                >
                  <option value="">Roleta Automática ({nextRepName})</option>
                  {activeReps.map(r => (
                    <option key={r.id} value={r.nome}>{r.nome}</option>
                  ))}
                </select>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-black hover:bg-gray-800 text-white px-5 py-2.5 text-sm font-bold rounded-xl transition-colors shadow-sm"
                >
                  Criar Lead
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
